// -----------------------------------------------------------
// FloraIQ - Hooks Reutilizáveis
// -----------------------------------------------------------

import { useState, useCallback, useMemo, useEffect } from "react";
import { api, campaignApi, ctaCatalogsApi, editorApi, iamApi, inspectionApi } from "@/lib/api";
import type { CtaCatalogsApi, QuadrasVegetationApi, TreeStatisticsQuery, VegetationSeriesApi } from "@/lib/api";
import {
  mapArborInspectionDetailFromApi,
  mapArborInspectionSummaryFromApi,
  mapAreaPilotoFromApi,
  mapAreaTreeInventoryFromApi,
  mapTreeStatisticsFromApi,
  mapCamadaGeoFromApi,
  mapDashboardSummary,
  mapDocumentFromApi,
  mapInspectionFromApi,
  mapInspectionQuadraLinkFromApi,
  mapPartyFromApi,
  mapProjectFromApi,
  mapQualitySummaryFromApi,
  mapQuadraFromApi,
  mapRascunhoFeicaoFromApi,
  mapSpeciesFromApi,
  mapTreeFromApi,
  mapTreeMetrics3DFromApi,
  mapValidationIssueFromApi,
  mapCampaignMemberFromApi,
  mapCampaignQuadraProgressFromApi,
  mapCampaignSummaryFromApi,
  mapDetectedObjectFromApi,
  mapFieldValidationNoteFromApi,
  mapTerritorialIndicatorsSummaryFromApi,
  mapDashboardOverviewFromApi,
  mapBairroFromApi,
  mapTerritorialInspectionDetailFromApi,
  mapTerritorialInspectionSummaryFromApi,
  type TreeMetric3DRow,
} from "@/lib/mappers";
import type { RascunhoFeicao } from "@/types/editor-territorial";

/** Hook de filtros genérico */
export function useFilters<T extends Record<string, string>>(defaults: T) {
  const [filters, setFilters] = useState<T>(defaults);

  const setFilter = useCallback((key: keyof T, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(defaults);
  }, [defaults]);

  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([k, v]) => v !== "" && v !== defaults[k]);
  }, [filters, defaults]);

  return { filters, setFilter, clearFilters, hasActiveFilters };
}

/** Paginação local de uma lista já carregada */
export function usePagination<T>(items: T[], pageSize = 10) {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const paginatedItems = items.slice((page - 1) * pageSize, page * pageSize);

  const goToPage = useCallback((p: number) => {
    setPage(Math.max(1, Math.min(p, totalPages)));
  }, [totalPages]);

  const nextPage = useCallback(() => goToPage(page + 1), [page, goToPage]);
  const prevPage = useCallback(() => goToPage(page - 1), [page, goToPage]);

  return { page, totalPages, paginatedItems, goToPage, nextPage, prevPage, total: items.length };
}

/** Estado de carregamento de uma chamada assíncrona */
export function useAsyncData<T>(fetcher: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  return { data, loading, error, reload: load };
}

export function useSpeciesList() {
  const fetcher = useCallback(() => api.species().then((rows) => rows.map(mapSpeciesFromApi)), []);
  return useRealList(fetcher);
}

export function useProjectsList() {
  const fetcher = useCallback(() => api.projects().then((rows) => rows.map(mapProjectFromApi)), []);
  return useRealList(fetcher);
}

/**
 * Lista so com dado real.  Base vazia e lista vazia: o CTA e a Vistoria sao telas de
 * operacao, e mostrar exemplares de demonstracao no lugar levaria o tecnico a erro.
 */
function useRealList<T>(fetcher: () => Promise<T[]>) {
  const { data, loading, error } = useApiData(fetcher, [fetcher]);
  return { items: data ?? [], loading, error, source: "api" as const };
}

export function useTreesList() {
  const fetcher = useCallback(() => api.trees().then((rows) => rows.map(mapTreeFromApi)), []);
  return useRealList(fetcher);
}

export function useTreeDetail(treeId: number | null) {
  const fetcher = useCallback(() => {
    if (!treeId) return Promise.resolve(null);
    return api.tree(treeId);
  }, [treeId]);
  return useApiData(fetcher, [treeId]);
}

export function useInspectionsList() {
  const fetcher = useCallback(() => api.inspections().then((rows) => rows.map(mapInspectionFromApi)), []);
  return useRealList(fetcher);
}

export function useDocumentsList() {
  const fetcher = useCallback(() => api.documents().then((rows) => rows.map(mapDocumentFromApi)), []);
  return useRealList(fetcher);
}

export function usePartiesList() {
  const fetcher = useCallback(() => api.parties().then((rows) => rows.map(mapPartyFromApi)), []);
  return useRealList(fetcher);
}

export function useQualityIssuesList() {
  const fetcher = useCallback(() => api.qualityIssues().then((rows) => rows.map(mapValidationIssueFromApi)), []);
  return useRealList(fetcher);
}

export function useQualityRankingList() {
  const fetcher = useCallback(() => api.qualitySummary().then((rows) => rows.map(mapQualitySummaryFromApi)), []);
  return useRealList(fetcher);
}

export function useTreeMetrics3DList() {
  const fetcher = useCallback(
    () => api.pointCloudTreeMetrics().then((rows) => rows.map(mapTreeMetrics3DFromApi)),
    []
  );
  return useRealList<TreeMetric3DRow>(fetcher);
}

/**
 * Busca dados reais sem fallback pra mock — usado em Território > Inspeções,
 * onde a ausência de dado deve virar empty state (importar JSON), não mock.
 */
function useApiData<T>(fetcher: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Erro ao carregar dados");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}

export function useTerritorialInspections() {
  const [refreshKey, setRefreshKey] = useState(0);
  const fetcher = useCallback(
    () => api.territorialInspections().then((rows) => rows.map(mapTerritorialInspectionSummaryFromApi)),
    []
  );
  const { data, loading, error } = useApiData(fetcher, [refreshKey]);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { inspections: data ?? [], loading, error, refetch };
}

export function useTerritorialInspectionDetail(inspectionCode: string | null) {
  const fetcher = useCallback(() => {
    if (!inspectionCode) return Promise.resolve(null);
    return api.territorialInspectionDetail(inspectionCode).then(mapTerritorialInspectionDetailFromApi);
  }, [inspectionCode]);
  return useApiData(fetcher, [inspectionCode]);
}

export function useTerritorialInspectionObjects(inspectionCode: string | null) {
  const fetcher = useCallback(() => {
    if (!inspectionCode) return Promise.resolve([]);
    return api.territorialInspectionObjects(inspectionCode).then((rows) => rows.map(mapDetectedObjectFromApi));
  }, [inspectionCode]);
  const { data, loading, error } = useApiData(fetcher, [inspectionCode]);
  return { objects: data ?? [], loading, error };
}

export function useTerritorialInspectionFieldValidationNotes(inspectionCode: string | null) {
  const fetcher = useCallback(() => {
    if (!inspectionCode) return Promise.resolve([]);
    return api
      .territorialInspectionFieldValidationNotes(inspectionCode)
      .then((rows) => rows.map(mapFieldValidationNoteFromApi));
  }, [inspectionCode]);
  const { data, loading, error } = useApiData(fetcher, [inspectionCode]);
  return { notes: data ?? [], loading, error };
}

export function useTerritorialIndicatorsSummary() {
  const fetcher = useCallback(
    () => api.territorialIndicatorsSummary().then(mapTerritorialIndicatorsSummaryFromApi),
    []
  );
  return useApiData(fetcher);
}

export function useEditorCamadas() {
  const fetcher = useCallback(() => editorApi.camadas().then((rows) => rows.map(mapCamadaGeoFromApi)), []);
  const { data, loading, error } = useApiData(fetcher);
  return { camadas: data ?? [], loading, error };
}

export interface EditorRascunhosFilters {
  status?: string;
  quadraRefId?: number;
  projetoRefId?: number;
  camadaDestino?: string;
}

export function useEditorRascunhos(filters: EditorRascunhosFilters = {}) {
  const [rascunhos, setRascunhos] = useState<RascunhoFeicao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return editorApi
      .rascunhos({
        status: filters.status,
        quadra_ref_id: filters.quadraRefId,
        projeto_ref_id: filters.projetoRefId,
        camada_destino: filters.camadaDestino,
      })
      .then((rows) => setRascunhos(rows.map(mapRascunhoFeicaoFromApi)))
      .catch((err) => setError(err instanceof Error ? err.message : "Erro ao carregar rascunhos"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.quadraRefId, filters.projetoRefId, filters.camadaDestino]);

  useEffect(() => {
    load();
  }, [load]);

  return { rascunhos, loading, error, refetch: load };
}

export function useAreasPiloto() {
  const fetcher = useCallback(() => api.areasPiloto().then((rows) => rows.map(mapAreaPilotoFromApi)), []);
  const { data, loading, error } = useApiData(fetcher);
  return { areasPiloto: data ?? [], loading, error };
}

export function useMunicipalities() {
  const fetcher = useCallback(() => api.municipalities(), []);
  const { data, loading, error } = useApiData(fetcher);
  return { municipalities: data ?? [], loading, error };
}

export function useClients() {
  const fetcher = useCallback(() => api.clients(), []);
  const { data, loading, error } = useApiData(fetcher);
  return { clients: data ?? [], loading, error };
}

export function useQuadrasList(areaPilotoId: number | null) {
  const fetcher = useCallback(
    () => api.quadras(areaPilotoId ?? undefined).then((rows) => rows.map(mapQuadraFromApi)),
    [areaPilotoId]
  );
  const { data, loading, error } = useApiData(fetcher, [areaPilotoId]);
  return { quadras: data ?? [], loading, error };
}

export function useInspectionsWithQuadras() {
  const fetcher = useCallback(
    () => api.inspectionsWithQuadras().then((rows) => rows.map(mapInspectionQuadraLinkFromApi)),
    []
  );
  const { data, loading, error } = useApiData(fetcher);
  return { links: data ?? [], loading, error };
}

// --- Arboricultural Inspections (Inventário Arbóreo / Vistoria Técnica) ---

export function useTreeArborInspections(treeId: number | null) {
  const [refreshKey, setRefreshKey] = useState(0);
  const fetcher = useCallback(() => {
    if (!treeId) return Promise.resolve([]);
    return inspectionApi.listByTree(treeId).then((rows) => rows.map(mapArborInspectionSummaryFromApi));
  }, [treeId]);
  const { data, loading, error } = useApiData(fetcher, [treeId, refreshKey]);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { inspections: data ?? [], loading, error, refetch };
}

export function useArborInspectionDetail(inspectionId: number | null) {
  const [refreshKey, setRefreshKey] = useState(0);
  const fetcher = useCallback(() => {
    if (!inspectionId) return Promise.resolve(null);
    return inspectionApi.detail(inspectionId).then(mapArborInspectionDetailFromApi);
  }, [inspectionId]);
  const { data, loading, error } = useApiData(fetcher, [inspectionId, refreshKey]);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { inspection: data, loading, error, refetch };
}

// --- Campaigns (Campanhas de Campo) ----------------------------------------

export function useCampaignsList(projectId?: number, status?: string) {
  const [refreshKey, setRefreshKey] = useState(0);
  const fetcher = useCallback(
    () => campaignApi.list({ project_id: projectId, status }).then((rows) => rows.map(mapCampaignSummaryFromApi)),
    [projectId, status]
  );
  const { data, loading, error } = useApiData(fetcher, [projectId, status, refreshKey]);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { campaigns: data ?? [], loading, error, refetch };
}

export function useCampaignDetail(campaignId: number | null) {
  const [refreshKey, setRefreshKey] = useState(0);
  const fetcher = useCallback(() => {
    if (!campaignId) return Promise.resolve(null);
    return campaignApi.get(campaignId).then(mapCampaignSummaryFromApi);
  }, [campaignId]);
  const { data, loading, error } = useApiData(fetcher, [campaignId, refreshKey]);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { campaign: data, loading, error, refetch };
}

export function useCampaignMembers(campaignId: number | null) {
  const [refreshKey, setRefreshKey] = useState(0);
  const fetcher = useCallback(() => {
    if (!campaignId) return Promise.resolve([]);
    return campaignApi.members(campaignId).then((rows) => rows.map(mapCampaignMemberFromApi));
  }, [campaignId]);
  const { data, loading, error } = useApiData(fetcher, [campaignId, refreshKey]);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { members: data ?? [], loading, error, refetch };
}

export function useCampaignQuadras(campaignId: number | null) {
  const [refreshKey, setRefreshKey] = useState(0);
  const fetcher = useCallback(() => {
    if (!campaignId) return Promise.resolve([]);
    return campaignApi.quadras(campaignId).then((rows) => rows.map(mapCampaignQuadraProgressFromApi));
  }, [campaignId]);
  const { data, loading, error } = useApiData(fetcher, [campaignId, refreshKey]);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { quadras: data ?? [], loading, error, refetch };
}

// ---------------------------------------------------------------------------
// IAM hooks
// ---------------------------------------------------------------------------

export function useIamPlatforms() {
  const fetcher = useCallback(() => iamApi.listPlatforms(), []);
  const { data, loading, error } = useApiData(fetcher);
  return { platforms: data ?? [], loading, error };
}

export function useIamModules(platform?: string) {
  const fetcher = useCallback(() => iamApi.listModules(platform), [platform]);
  const { data, loading, error } = useApiData(fetcher, [platform]);
  return { modules: data ?? [], loading, error };
}

export function useIamPermissions() {
  const fetcher = useCallback(() => iamApi.listPermissions(), []);
  const { data, loading, error } = useApiData(fetcher);
  return { permissions: data ?? [], loading, error };
}

export function useIamRoles() {
  const fetcher = useCallback(() => iamApi.listRoles(), []);
  const { data, loading, error } = useApiData(fetcher);
  return { roles: data ?? [], loading, error };
}

export function useIamAccessManifest() {
  const fetcher = useCallback(() => iamApi.getAccessManifest(), []);
  const { data, loading, error } = useApiData(fetcher);
  return { manifest: data ?? null, loading, error };
}

export function useIamOrganizationMembers(organizationId: number | null) {
  const fetcher = useCallback(() => {
    if (!organizationId) return Promise.resolve([]);
    return iamApi.listOrganizationMembers(organizationId);
  }, [organizationId]);
  const { data, loading, error } = useApiData(fetcher, [organizationId]);
  return { members: data ?? [], loading, error };
}

export function useIamAuditEvents(params?: {
  limit?: number;
  offset?: number;
  event_type?: string;
  target_user_id?: number;
  organization_id?: number;
}) {
  const paramsKey = JSON.stringify(params ?? {});
  const fetcher = useCallback(() => iamApi.listAuditEvents(params), [paramsKey]);
  const { data, loading, error } = useApiData(fetcher, [paramsKey]);
  return { events: data?.events ?? [], limit: data?.limit ?? 50, offset: data?.offset ?? 0, loading, error };
}

export function useDashboardOverview() {
  const fetcher = useCallback(() => api.dashboardOverview().then(mapDashboardOverviewFromApi), []);
  return useApiData(fetcher);
}


export function useBairros() {
  const fetcher = useCallback(() => api.bairros().then((rows) => rows.map(mapBairroFromApi)), []);
  return useApiData(fetcher);
}

export function useSatelliteVegetationQuadras(areaPilotoId: number | null) {
  const fetcher = useCallback(
    (): Promise<QuadrasVegetationApi | null> =>
      areaPilotoId == null ? Promise.resolve(null) : api.satelliteVegetationQuadras(areaPilotoId),
    [areaPilotoId],
  );
  return useApiData(fetcher, [areaPilotoId]);
}

export function useVegetationSeries(kind: "quadra" | "area", id: number | null) {
  const fetcher = useCallback((): Promise<VegetationSeriesApi | null> => {
    if (id == null) return Promise.resolve(null);
    return kind === "quadra" ? api.quadraSatelliteVegetation(id) : api.areaSatelliteVegetation(id);
  }, [kind, id]);
  return useApiData(fetcher, [kind, id]);
}

export function useBairrosGeojson() {
  const fetcher = useCallback(() => api.bairrosGeojson(), []);
  return useApiData(fetcher);
}


// Catalogos do CTA (F1-F4, R1-R4, A0-A2): vem do servidor, que e a unica fonte dos
// rotulos, das definicoes e da origem de cada escala.  Buscados uma vez por sessao.
let ctaCatalogsCache: CtaCatalogsApi | null = null;

export function useCtaCatalogs() {
  const [catalogs, setCatalogs] = useState<CtaCatalogsApi | null>(ctaCatalogsCache);

  useEffect(() => {
    if (ctaCatalogsCache) return;
    let active = true;
    ctaCatalogsApi
      .get()
      .then((data) => {
        ctaCatalogsCache = data;
        if (active) setCatalogs(data);
      })
      .catch(() => {
        /* sem catalogo a tela usa os rotulos locais; nada deixa de funcionar */
      });
    return () => {
      active = false;
    };
  }, []);

  return catalogs;
}


export function useTreeTimeline(treeId: number | null) {
  const fetcher = useCallback(() => (treeId ? inspectionApi.treeTimeline(treeId) : Promise.resolve([])), [treeId]);
  const { data, loading, error } = useApiData(fetcher, [treeId]);
  return { events: data ?? [], loading, error };
}

export function useTreeLotRelation(treeId: number | null, version = 0) {
  const fetcher = useCallback(
    () => (treeId ? campaignApi.treeLotRelation(treeId) : Promise.resolve(null)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [treeId, version],
  );
  return useApiData(fetcher, [treeId, version]);
}

export function useTreeEvidence(treeId: number | null) {
  const [refreshKey, setRefreshKey] = useState(0);
  const fetcher = useCallback(() => (treeId ? inspectionApi.treeEvidence(treeId) : Promise.resolve([])), [treeId]);
  const { data, loading, error } = useApiData(fetcher, [treeId, refreshKey]);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { evidence: data ?? [], loading, error, refetch };
}

export function useAreaTreeInventory(areaPilotoId: number | null) {
  const fetcher = useCallback(() => {
    if (!areaPilotoId) return Promise.resolve(null);
    return api.areaTreeInventory(areaPilotoId).then(mapAreaTreeInventoryFromApi);
  }, [areaPilotoId]);
  return useApiData(fetcher, [areaPilotoId]);
}

export function useTreeStatistics(query: TreeStatisticsQuery) {
  const key = JSON.stringify(query);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetcher = useCallback(() => api.treeStatistics(query).then(mapTreeStatisticsFromApi), [key]);
  return useApiData(fetcher, [key]);
}
